import { Head, useForm } from '@inertiajs/react'
import { BRAND_NAME } from '@/lib/brand'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { PublicKegPreview } from '@/components/organizations/PublicKegPreview'

type Labels = {
  eyebrow: string
  title: string
  name_label: string
  name_placeholder: string
  contact_heading: string
  contact_hint: string
  contact_email: string
  contact_email_placeholder: string
  contact_phone: string
  contact_phone_placeholder: string
  preview_caption: string
  preview_owned_by: string
  preview_brewery_placeholder: string
  preview_no_contact: string
  preview_email_label: string
  preview_phone_label: string
  submit: string
  submit_processing: string
}

type Props = {
  labels: Labels
}

export default function OrganizationNew({ labels }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    contact_email: '',
    contact_phone: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/organizations')
  }

  return (
    <>
      <Head title={labels.title} />
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12 sm:px-6 lg:px-8">
        <p className="text-center text-xs font-extrabold uppercase tracking-[0.1em] text-ink-faint">{labels.eyebrow}</p>
        <h1 className="mt-2 text-center font-display text-3xl leading-tight text-ink">{labels.title}</h1>

        <Card className="mt-8 p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <Field
              label={labels.name_label}
              name="name"
              type="text"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              placeholder={labels.name_placeholder}
              required
              autoFocus
              error={errors.name}
            />

            {/* The heading carries the explanation, so no microcopy sits above the fields: the
                grouping says what these details are for, and the preview below shows it. */}
            {/* The heading is a <p>, not a visible <legend>: a legend sits inside the border box
                and punches a gap through the top rule. sr-only keeps the grouping announced. */}
            <fieldset className="space-y-4 border-t border-line pt-6">
              <legend className="sr-only">{labels.contact_heading}</legend>
              <p className="font-display text-xl leading-snug text-ink">{labels.contact_heading}</p>

              <Field
                label={labels.contact_email}
                name="contact_email"
                type="email"
                value={data.contact_email}
                onChange={(e) => setData('contact_email', e.target.value)}
                placeholder={labels.contact_email_placeholder}
                error={errors.contact_email}
              />
              <Field
                label={labels.contact_phone}
                name="contact_phone"
                type="tel"
                value={data.contact_phone}
                onChange={(e) => setData('contact_phone', e.target.value)}
                placeholder={labels.contact_phone_placeholder}
                error={errors.contact_phone}
              />
              <p className="text-sm text-ink-soft">{labels.contact_hint}</p>

              <div className="pt-1">
                <PublicKegPreview
                  name={data.name}
                  contactEmail={data.contact_email}
                  contactPhone={data.contact_phone}
                  labels={labels}
                />
              </div>
            </fieldset>

            <Button type="submit" disabled={processing} className="w-full disabled:opacity-60">
              {processing ? labels.submit_processing : labels.submit}
            </Button>
          </form>
        </Card>

        <p className="mt-8 text-center font-display text-sm text-ink-faint">{BRAND_NAME}</p>
      </div>
    </>
  )
}
